/**
 * Subscription levels (web-pose-extraction step 3).
 *
 * Replaces the ad-hoc "email starts with olly.dolan" checks scattered through the frontend with
 * one resolver + per-level policies. PREMIUM exists for the coming tiering work; today auth0 gives
 * us only the email, so ADMIN = the named internal accounts and everyone else is FREE.
 */
export enum SubscriptionLevel {
  FREE = "FREE",
  PREMIUM = "PREMIUM",
  ADMIN = "ADMIN",
}

// The internal accounts, matched exactly (case-insensitive, trimmed). Mirrored in SubscriptionPolicy.AdminEmails.
// No prefix (owner, 2026-10-21): "olly.dolan*" made olly.dolan@gmail.com, a free account, premium, and would have
// made anyone who signed up as olly.dolan.anything@anywhere an admin.
const ADMIN_EMAILS = ["olly.dolan@googlemail.com", "sofia@sjoeberg.net"];

export function subscriptionLevelForEmail(email: string | null | undefined): SubscriptionLevel {
  const e = email?.trim().toLowerCase();
  if (e && ADMIN_EMAILS.includes(e)) return SubscriptionLevel.ADMIN;
  return SubscriptionLevel.FREE;
}

/** Upload duration cap — unchanged semantics from the old inline check (public 20s, internal 90s). */
export function maxUploadDurationSec(level: SubscriptionLevel): number {
  return level === SubscriptionLevel.ADMIN ? 90 : 20;
}

/** "Extract pose on this device" defaults ON for everyone except ADMIN (internal accounts keep
 *  exercising the full GPU path by default). */
export function defaultsToClientExtraction(level: SubscriptionLevel): boolean {
  return level !== SubscriptionLevel.ADMIN;
}

/** Only ADMIN may opt OUT of on-device extraction. Basic users are front-end labels only — the
 *  toggle is not shown and extraction always runs; the server stamps their uploads gpuBypass. */
export function canToggleClientExtraction(level: SubscriptionLevel): boolean {
  return level === SubscriptionLevel.ADMIN;
}

/** What the upload asks the server for. Client-side pose extraction runs for EVERY upload;
 *  "free" = analysis on those labels (positions, stills, scorecard), "premium" = the in-depth
 *  run (GPU labels + narrated video). Mirrors SubscriptionPolicy.GpuBypassFor server-side,
 *  which also gates the claim — the UI choice is convenience, not authority. */
export type AnalysisMode = "free" | "premium";

export function canSelectPremium(level: SubscriptionLevel): boolean {
  return level === SubscriptionLevel.PREMIUM || level === SubscriptionLevel.ADMIN;
}

export function defaultAnalysisMode(level: SubscriptionLevel): AnalysisMode {
  return canSelectPremium(level) ? "premium" : "free";
}

/** The analysis an upload will ask for: what the uploader chose if they are entitled to choose,
 *  their level's default if they have not chosen, and always "free" for a level that cannot pick
 *  Premium. Derived on every render from the CURRENT level — never frozen at mount: auth0 hands
 *  the account over a moment after the page mounts, and a default frozen then started the same
 *  entitled account on Free or on Premium depending on timing (dev 18827/18828, 2026-10-03). */
export function resolveAnalysisMode(level: SubscriptionLevel, chosen: AnalysisMode | null): AnalysisMode {
  if (!canSelectPremium(level)) return "free";
  return chosen ?? defaultAnalysisMode(level);
}

const ANALYSIS_MODE_KEY = "eleetz.analysisMode";

/** The uploader's last Free/Premium pick on this device, or null. Someone uploading a batch picks
 *  once; the page coming back on its default for every next clip silently undid that pick. A
 *  per-device convenience only: the server still gates the claim against entitlement. */
export function rememberedAnalysisMode(): AnalysisMode | null {
  try {
    const stored = window.localStorage.getItem(ANALYSIS_MODE_KEY);
    return stored === "free" || stored === "premium" ? stored : null;
  } catch {
    return null; // storage blocked or unavailable: fall back to the level's default
  }
}

export function rememberAnalysisMode(mode: AnalysisMode): void {
  try {
    window.localStorage.setItem(ANALYSIS_MODE_KEY, mode);
  } catch {
    // storage blocked or unavailable: the pick still holds for this page
  }
}
